import React, { useContext, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../../../context/AuthContext';
import { DataContext } from '../../../context/DataContext';
import { getUserActivityStats } from '../../../firebase/services';
import './profile.css';
import { BottomNav } from '../../common/BottomNav';
import MonthlyGoalsCard from './MonthlyGoalsCard';

const ProfileScreen = () => {
  const { t } = useTranslation();
  const { currentUser, logout, updateUserProfile } = useContext(AuthContext);
  const { causes } = useContext(DataContext);
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [activityStats, setActivityStats] = useState({ streetlight: 0, daylight: 0, sunshield: 0, total: 0 });
  const [installMessage, setInstallMessage] = useState('');

  const handleInstallApp = () => {
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone;
    if (isStandalone) {
      setInstallMessage(t('settings.alreadyInstalled'));
      setTimeout(() => setInstallMessage(''), 3000);
    } else {
      localStorage.removeItem('lightway-install-dismissed');
      window.dispatchEvent(new Event('lightway-show-install'));
      setInstallMessage(t('settings.installHint'));
      setTimeout(() => setInstallMessage(''), 5000);
    }
  };

  useEffect(() => {
    if (currentUser) {
      setName(currentUser.displayName || '');
      setEmail(currentUser.email || '');
    }
  }, [currentUser]);

  useEffect(() => {
    const fetchStats = async () => {
      if (currentUser) {
        const stats = await getUserActivityStats(currentUser.uid);
        setActivityStats(stats);
      }
    };
    fetchStats();
  }, [currentUser]);

  const handleEditToggle = async () => {
    if (isEditing) {
      try {
        await updateUserProfile(currentUser, { displayName: name, email: email });
      } catch (error) {
        console.error('Failed to update profile:', error);
      }
    }
    setIsEditing(!isEditing);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };

  if (!currentUser) {
    return <div>{t('common.loading')}</div>;
  }

  return (
    <div className="profile-container">
      <div className="profile-header">
        <h2>{t('profile.title')}</h2>
      </div>
      <div className="profile-card">
        <div className="profile-avatar">
          <span>{name.charAt(0).toUpperCase()}</span>
        </div>
        <div className="profile-info">
          <div className="profile-info-header">
            {isEditing ? (
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="profile-name-input"
              />
            ) : (
              <h3>{name}</h3>
            )}
            <button onClick={handleEditToggle} className="edit-button">
              {isEditing ? t('profile.save') : t('profile.edit')}
            </button>
          </div>
          {isEditing ? (
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="profile-email-input"
            />
          ) : (
            <p>{email}</p>
          )}
          <p>Member since {new Date(currentUser.metadata.creationTime).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</p>
        </div>
      </div>

      {/* Advocacy Journey Section */}
      <div className="profile-section">
        <h4>{t('profile.advocacyJourney')}</h4>
        <div className="advocacy-stats">
          <div className="stat-item">
            <span className="stat-number">{activityStats.total}</span>
            <span className="stat-label">{t('profile.activities')}</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">{activityStats.streetlight}</span>
            <span className="stat-label">{t('profile.streetlight')}</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">{activityStats.daylight}</span>
            <span className="stat-label">{t('profile.daylight')}</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">{activityStats.sunshield}</span>
            <span className="stat-label">{t('profile.sunshield')}</span>
          </div>
        </div>
        {causes && causes.length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <MonthlyGoalsCard causes={causes} userId={currentUser.uid} />
          </div>
        )}
        <Link to="/advocacy-summary" className="advocacy-summary-link">
          {t('profile.advocacySummary')} →
        </Link>
        <button
          className="elevation-link"
          onClick={() => navigate('/elevation')}
        >
          <span>▲</span>
          <span>{t('profile.requestElevation')}</span>
        </button>
      </div>

      <div className="profile-section">
        <h4>{t('profile.settings')}</h4>
        <Link to="/settings/notifications" className="profile-menu-item">
          <span>{t('profile.notificationPreferences')}</span>
          <span>{'>'}</span>
        </Link>
        <Link to="/settings/privacy" className="profile-menu-item">
          <span>{t('profile.privacySettings')}</span>
          <span>{'>'}</span>
        </Link>
        <Link to="/settings/help" className="profile-menu-item">
          <span>{t('profile.helpFeedback')}</span>
          <span>{'>'}</span>
        </Link>
        <Link to="/settings/language" className="profile-menu-item">
          <span>{t('settings.language')}</span>
          <span>{'>'}</span>
        </Link>
        <Link to="/my-templates" className="profile-menu-item">
          <span>{t('profile.myTemplates')}</span>
          <span>{'>'}</span>
        </Link>
        <button onClick={handleInstallApp} className="profile-menu-item" style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', font: 'inherit' }}>
          <span>{t('settings.installApp')}</span>
          <span>{'>'}</span>
        </button>
        {installMessage && (
          <p style={{ fontSize: '0.85rem', color: '#28a745', margin: '8px 0 0', paddingLeft: '4px' }}>
            {installMessage}
          </p>
        )}
      </div>
      <div className="logout-section">
        <button onClick={handleLogout} className="logout-button">
          {t('profile.signOut')}
        </button>
      </div>
      <BottomNav />
    </div>
  );
};

export default ProfileScreen;
